import PermissionCheck from 'common/features/permission-check';
import Table from 'common/table/table';
import { ThemeHeader } from 'common/table/table-helper';
import { ToggleFilter, Toolbar } from 'common/toolbar';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { Button, SapId } from 'ui';
import { withRouter } from 'utils/withRouter';

const Wrapper = styled.div`
  background-color: #fff;
`;

const prefix = 'dashboard.functionalLocationsTable';

const FunctionalLocationsTable = ({
  isLoading,
  functionalLocations,
  intl,
  history,
  activeRow,
}) => {
  const [selectedFunctionalLocations, setSelectedFunctionalLocations] = useState([]);
  const [toggleTypeValue, setToggleTypeValue] = useState('all');

  const toggleType = [
    {
      text: intl.formatMessage({ id: `${prefix}.filter.ALL` }),
      value: 'all',
    },
    {
      text: intl.formatMessage({ id: `${prefix}.filter.UR` }),
      value: 'ur',
    },
    {
      text: intl.formatMessage({ id: `${prefix}.filter.DS` }),
      value: 'ds',
    },
  ];

  const getColumns = () => {
    const { formatMessage: translate } = intl;

    return [
      {
        displayName: translate({ id: `${prefix}.NUMBER_COL` }),
        fieldName: 'number',
        renderFn: row => <SapId originalId={row.number}>{id => <>{id}</>}</SapId>,
        width: '250px',
      },
      {
        displayName: translate({ id: `${prefix}.DESCRIPTION_COL` }),
        fieldName: 'name',
        tooltip: row => row.name,
      },
      {
        displayName: translate({ id: `${prefix}.TYPE_COL` }),
        fieldName: 'typeDescription',
        width: '250px',
      },
      {
        displayName: translate({ id: `${prefix}.STATUS_COL` }),
        fieldName: 'status',
        width: '250px',
      },
      {
        displayName: translate({ id: `${prefix}.AGE_COL` }),
        renderFn: v => `${v?.age} yr`,
        width: '150px',
      },
      {
        displayName: translate({ id: `${prefix}.INSTALLATION_DATE_COL` }),
        renderFn: v => v?.installationDate && intl.formatDate(v?.installationDate),
        width: '250px',
      },
    ];
  };

  const getFilteredAndSortedData = () => functionalLocations
    ?.filter((fl) => {
      switch (toggleTypeValue) {
        case 'ds':
          return fl.type === 'distributionSystem';
        case 'ur':
          return fl.type === 'utilityRoom';
        default:
          return true;
      }
    });

  const handleRowSelect = (v) => {
    if (selectedFunctionalLocations.find(e => e === v)) {
      setSelectedFunctionalLocations(s => s.filter(f => f !== v));
    } else {
      setSelectedFunctionalLocations(s => [...s, v]);
    }
  };

  const handleRowClick = (id) => {
    history.push(`/functionallocation/${id}`, { context: id });
  };

  const handleTypeChange = e => setToggleTypeValue(e.target.value);

  const handleCreateOrderClick = () => {
    history.push(`create-order/functionallocation/${selectedFunctionalLocations.join(',')}`);
  };

  const renderToolbar = () => {
    const { formatMessage } = intl;

    return (
      <Toolbar>
        <Toolbar.Item showAsQuickAccess={true}>
          <PermissionCheck permissionsList={['ordercreate']}>
            <Button
              type='secondary'
              dark={true}
              activeColor='#007AA4'
              onClick={handleCreateOrderClick}
              disabled={!selectedFunctionalLocations.length}
            >
              <FormattedMessage id='dashboard.CREATE_ORDER_BTN' />
            </Button>
          </PermissionCheck>
        </Toolbar.Item>
        <Toolbar.Item showAsQuickAccess={true}>
          <ToggleFilter
            value={toggleTypeValue}
            items={toggleType}
            dark={true}
            activeColor='#007AA4'
            label={formatMessage({ id: `${prefix}.toolbar.TYPE_LABEL` })}
            onChange={handleTypeChange}
          />
        </Toolbar.Item>
      </Toolbar>
    );
  };

  const renderHeader = () => <ThemeHeader inlineTable={true}>
    <span>
      {`${intl.formatMessage({ id: `${prefix}.TABLE_TITLE` })} `}
      ({functionalLocations?.length})
    </span>
    {renderToolbar()}
  </ThemeHeader>;

  const filteredAndSortedData = getFilteredAndSortedData();

  return (
    <Wrapper>
      <Table
        renderCustomHeader={renderHeader}
        usePaging={true}
        fullWidth={true}
        columns={getColumns()}
        data={filteredAndSortedData}
        isLoading={isLoading}
        selectedRowIds={selectedFunctionalLocations}
        onRowSelect={handleRowSelect}
        onRowClick={handleRowClick}
        activeRow={activeRow}
        lightHeader={true}
      />
    </Wrapper>
  );
};

FunctionalLocationsTable.propTypes = {
  isLoading: PropTypes.bool,
  functionalLocations: PropTypes.array,
  intl: PropTypes.object,
  history: PropTypes.object.isRequired,
  activeRow: PropTypes.object,
};

export default withRouter(injectIntl(FunctionalLocationsTable));
